// src/components/MintNFTComponent.js
/* global BigInt */
import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    updateDoc,
    setDoc,
    serverTimestamp,
    arrayUnion,
    doc,
    getDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { spaceTokenListName, spaceTokenListDocumentId } from '../../functions/spaceTokenListPolicy.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { useGateAccess } from '../hooks/useGateAccess';
import DigitalAssetPurchase from './DigitalAssetPurchase';
import MintNftDetails from './MintNftDetails';
import MintProgress from './MintProgress';
import { getLegacyMintStages } from '../utils/mintProgress';
import { readMintUploads, saveMintUploads, clearMintUploads } from '../utils/mintUploadCache';
import { useCreatorPublishing } from '../hooks/useCreatorPublishing';
import { assertPublishingNetwork, callDigitalAssets, executePublishingOperation, formatAtomic, pendingPublishingOperation } from '../utils/digitalAssetsClient';
import ComponentControls from './ComponentControls';
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";

// Umi / Metaplex Core
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import {
    generateSigner,
    createGenericFile,
    sol,
    lamports,
    publicKey as umiPublicKey,
    transactionBuilder,
} from '@metaplex-foundation/umi';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';
import { base58 } from '@metaplex-foundation/umi/serializers';
import { create as createCoreNft, addPlugin, mplCore } from '@metaplex-foundation/mpl-core';

// Irys uploader only
import { irysUploader } from '@metaplex-foundation/umi-uploader-irys';

// SOL transfers (fees)
import { transferSol } from '@metaplex-foundation/mpl-toolbox';

// NEW: typography resolver
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';

const DEVNET_IRYS = 'https://devnet.irys.xyz';
const MAINNET_IRYS = 'https://uploader.irys.xyz';


// ---------------------------------------------------------
// Royalties helpers (safe, strict, matches Core requirements)
// ---------------------------------------------------------

// Convert 0..20% --> 0..2000 basis points.
const pctToBp = (pct) => {
    const n = Number(pct);
    const clamped = Number.isFinite(n) ? Math.max(0, Math.min(20, n)) : 0;
    return Math.round(clamped * 100);
};

/**
 * Normalize royalties coming from comp.royalties:
 *   comp.royalties = {
 *     percentage: <0..20>,        // total royalties for the asset
 *     creators: [{ address, percentage }] // percentages that must sum <= 100; SPACE_OWNER row is optional
 *   }
 *
 * - Always includes the MuultiSpace creator (spaceOwnerPk).
 * - Additional creators are optional; invalid addresses are dropped.
 * - Dedupes addresses (merges shares).
 * - Converts shares to INTEGERS that sum to EXACTLY 100 as required by Core.
 * - If total basis points == 0, returns { basisPoints:0, creators:[] } (no plugin added).
 */
const normalizeRoyalties = (spaceOwnerPk, royalties, toUmiPk) => {
    const totalBp = pctToBp(royalties?.percentage ?? 7);
    if (totalBp <= 0) return { basisPoints: 0, creators: [] };

    const raw = Array.isArray(royalties?.creators) ? royalties.creators : [];
    const others = [];

    for (const c of raw) {
        const pct = Number(c?.percentage ?? 0);
        if (!Number.isFinite(pct) || pct <= 0) continue;

        const rawAddr = String(c?.address || '').trim();
        if (!rawAddr || rawAddr === 'SPACE_OWNER') continue; // SPACE_OWNER handled separately

        try {
            const addr = toUmiPk(rawAddr).toString();
            others.push({ address: addr, pct });
        } catch {
            // Bad address -> skip
        }
    }

    // Clamp others' total to 99 to keep >=1 for owner.
    let sumOthers = others.reduce((s, x) => s + x.pct, 0);
    if (sumOthers > 99) {
        others.forEach((o) => (o.pct = (o.pct / sumOthers) * 99));
        sumOthers = 99;
    }

    let ownerPct = 100 - Math.round(sumOthers);
    if (ownerPct < 1) ownerPct = 1;

    // Merge duplicates (including owner if someone added same address).
    const buckets = new Map();
    const addShare = (addr, pct) => {
        const k = toUmiPk(addr).toString();
        buckets.set(k, (buckets.get(k) ?? 0) + pct);
    };
    addShare(spaceOwnerPk, ownerPct);
    for (const o of others) addShare(o.address, o.pct);

    // Integer rounding + drift correction to ensure sum === 100.
    let creators = [...buckets.entries()].map(([address, pct]) => ({
        address,
        percentage: Math.max(0, Math.round(pct)),
    }));
    let drift = 100 - creators.reduce((s, c) => s + c.percentage, 0);
    if (drift !== 0 && creators.length > 0) {
        creators[0].percentage = Math.max(0, creators[0].percentage + drift);
    }
    creators = creators.filter((c) => c.percentage > 0);
    if (creators.length === 0) {
        creators = [{ address: toUmiPk(spaceOwnerPk).toString(), percentage: 100 }];
    }

    // Map to Umi PublicKeys.
    const creatorsForPlugin = creators.map((c) => ({
        address: toUmiPk(c.address),
        percentage: c.percentage,
    }));

    return { basisPoints: totalBp, creators: creatorsForPlugin };
};

export default function MintNFTComponent({
                                             comp,
                                             index,
                                             moveUp,
                                             moveDown,
                                             onEdit,
                                             onMintCollectionChange,
                                             isEditor = false,
                                             removeComponent,

                                             // Gate props like TextComponent
                                             onGateTypeChange,
                                             onGateListChange,
                                             donationCampaignOptions = [],
                                             donationGate,
                                             onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,
                                             style = {},
                                             onStyleChange,
                                             coverUrl = '',
                                             onCoverChange,
                                             onPanelBackgroundImageChange,
                                             timestampDisplay = 'none',
                                             onTimestampDisplayChange,

                                             // NEW (optional): global typography
                                             pageTypography = null,
                                             ownerPublicKey = '',
                                         }) {
    // Gate shape & access (match TextComponent)
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || { type: 'public', listId: '' });
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const logic = comp.gateLogic === 'all' ? 'all' : 'any';

    const [gateRefreshKey, setGateRefreshKey] = useState(0);

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const [hasAccess, isLoading, gateNamesLabel, , gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;

    // Wallet / network
    const walletContext = useWallet();
    const { publicKey, connected, wallet } = walletContext;
    const rpcEndpoint = process.env.REACT_APP_RPC_ENDPOINT || 'https://api.devnet.solana.com';
    const [legacyQuote, setLegacyQuote] = useState(null);
    const isDevnet = legacyQuote?.cluster ? legacyQuote.cluster === 'devnet' : rpcEndpoint.includes('devnet');

    // Irys node (no selector in UI)
    const irysAddress = isDevnet ? DEVNET_IRYS : MAINNET_IRYS;

    // Route/user context
    const { username: routeUsername } = useParams();
    const [muultiName, setMuultiName] = useState(routeUsername || '');
    const [pageTokenListName, setPageTokenListName] = useState('');

    // Space owner publicKey (Update Delegate + creator proceeds + royalties)
    const [spaceOwnerPk, setSpaceOwnerPk] = useState('');
    const { offers, collections = [] } = useCreatorPublishing(ownerPublicKeyStr || spaceOwnerPk);
    const digitalAssetOffer = offers.find(item => String(item.componentId) === String(comp.id) && item.status !== 'DRAFT');
    const mintUploads = useRef({});



    // Resolve muultiName (editor fallback)
    useEffect(() => {
        (async () => {
            if (ownerPublicKeyStr || muultiName || !connected || !publicKey) return;
            try {
                const usersRef = collection(db, 'users');
                const qUser = query(usersRef, where('publicKey', '==', ownerPublicKeyStr || publicKey.toString()));
                const snap = await getDocs(qUser);
                if (!snap.empty) {
                    const data = snap.docs[0].data();
                    if (data?.muultiName) setMuultiName(data.muultiName);
                    if (data?.publicKey) setSpaceOwnerPk(data.publicKey);
                    setPageTokenListName(spaceTokenListName(data.publicKey, data));
                }
            } catch (e) {
                console.warn('Could not resolve muultiName:', e);
            }
        })();
    }, [connected, publicKey, muultiName, ownerPublicKeyStr]);

    // Resolve the MuultiSpace creator's publicKey
    useEffect(() => {
        (async () => {
            try {
                if (ownerPublicKeyStr) {
                    setSpaceOwnerPk(ownerPublicKeyStr);
                    const profile = await getDoc(doc(db, 'users', ownerPublicKeyStr));
                    if (profile.exists()) {
                        const data = profile.data();
                        setPageTokenListName(spaceTokenListName(ownerPublicKeyStr, data));
                        setMuultiName(previous => previous || data.muultiName || '');
                    }
                    return;
                }
                const usersRef = collection(db, 'users');
                const q = routeUsername ? query(usersRef, where('muultiName', '==', routeUsername)) : null;
                if (q) {
                    const snap = await getDocs(q);
                    if (!snap.empty) {
                        const data = snap.docs[0].data();
                        if (data?.publicKey) {
                            setSpaceOwnerPk(data.publicKey);
                            setPageTokenListName(spaceTokenListName(data.publicKey, data));
                            return;
                        }
                    }
                }
            } catch (e) {
                console.warn('Could not resolve space owner publicKey from users:', e);
            }

        })();
    }, [routeUsername, ownerPublicKeyStr]);

    // Load template
    const [template, setTemplate] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const [error, setError] = useState('');
    const fundingTxSig = error.match(/failed to post funding tx - ([1-9A-HJ-NP-Za-km-z]{87,88})/)?.[1];
    const [status, setStatus] = useState('');

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoadingTemplate(true);
                if (!comp?.templateId) throw new Error('Missing templateId on component.');
                const ref = doc(db, 'nftMetadataTemplates', comp.templateId);
                const snap = await getDoc(ref);
                if (!snap.exists()) throw new Error('Template not found.');
                const data = snap.data();
                if (!data?.imageUrl) throw new Error('Template must include an imageUrl.');
                if (mounted) {
                    setTemplate({ id: snap.id, ...data });
                    if (!ownerPublicKeyStr && !routeUsername && data.userId) setSpaceOwnerPk(String(data.userId));
                }
            } catch (e) {
                console.error(e);
                if (mounted) setError(e.message || 'Failed to load template.');
            } finally {
                if (mounted) setLoadingTemplate(false);
            }
        })();
        return () => void (mounted = false);
    }, [comp?.templateId, ownerPublicKeyStr, routeUsername]);

    // Umi (Irys-only)
    const umi = useMemo(() => {
        const base = createUmi(rpcEndpoint).use(mplCore());
        // Keep Irys funding preflight enabled, as in collection creation.
        const sendTransaction = base.rpc.sendTransaction.bind(base.rpc);
        base.rpc.sendTransaction = (transaction, options = {}) => sendTransaction(transaction, {
            ...options, skipPreflight: false, preflightCommitment: 'confirmed', maxRetries: options.maxRetries ?? 5,
        });
        if (wallet?.adapter) base.use(walletAdapterIdentity(wallet.adapter));
        base.use(irysUploader({ address: irysAddress }));
        return base;
    }, [wallet?.adapter, rpcEndpoint, irysAddress]);

    // Devnet airdrop
    const [airdropping, setAirdropping] = useState(false);
    const handleAirdrop = async () => {
        if (!isDevnet || !umi?.identity?.publicKey) return;
        try {
            setAirdropping(true);
            setStatus('Requesting 1 SOL airdrop…');
            await assertPublishingNetwork('devnet', rpcEndpoint);
            await umi.rpc.airdrop(umi.identity.publicKey, sol(1));
            setStatus('Airdropped 1 SOL on devnet.');
        } catch (e) {
            console.error(e);
            setError('Airdrop failed (RPC rate limited?). Try again later.');
        } finally {
            setAirdropping(false);
        }
    };

    // Template image -> GenericFile (no file picker)
    const toGenericFile = useCallback(async () => {
        const res = await fetch(template.imageUrl, { mode: 'cors' });
        if (!res.ok) throw new Error('Failed to fetch template image.');
        const blob = await res.blob();
        const array = new Uint8Array(await blob.arrayBuffer());
        const inferred = blob.type || 'image/png';
        return createGenericFile(array, 'image', {
            tags: [{ name: 'Content-Type', value: inferred }],
        });
    }, [template?.imageUrl]);

    // Checklist UI
    const [steps, setSteps] = useState({
        imageUploaded: false,
        metadataUploaded: false,
        serviceFeePaid: false,
        creatorFeePaid: false,
        nftCreated: false,
        setUpdateDelegate: false,
        setRoyalties: false,
        addedToNftList: false,
        addedToPageList: false,
    });

    const [minting, setMinting] = useState(false);
    const [txSig, setTxSig] = useState('');
    const [assetPubkey, setAssetPubkey] = useState('');
    const [imageUri, setImageUri] = useState('');
    const [metadataUri, setMetadataUri] = useState('');

    // Firestore: upsert token into a gate list
    const upsertGateListWithToken = async (listName, tokenId, spaceOwner = '') => {
        if (!listName || !tokenId) return;
        const gateListsRef = collection(db, 'gateLists');
        const qList = query(gateListsRef, where('name', '==', listName), where('gateType', '==', 'token'));
        const snap = await getDocs(qList);

        if (snap.empty && spaceOwner) {
            // Backend collection mints use this same ID for a new Space list.
            await setDoc(doc(db, 'gateLists', spaceTokenListDocumentId(spaceOwner)), {
                name: listName, gateType: 'token', hashList: arrayUnion(tokenId),
                createdAt: serverTimestamp(), auto: true,
            }, { merge: true });
        } else if (snap.empty) {
            await addDoc(gateListsRef, {
                name: listName,
                gateType: 'token',
                hashList: [tokenId],
                createdAt: serverTimestamp(),
                auto: true,
            });
        } else {
            const targets = spaceOwner ? snap.docs : [snap.docs[0]];
            await Promise.all(targets.map(list => updateDoc(list.ref, {
                hashList: arrayUnion(tokenId),
                updatedAt: serverTimestamp(),
            })));
        }
    };

    // Mint flow
    const handleMint = async () => {
        setError('');
        setStatus('');
        setTxSig('');
        setAssetPubkey('');
        setImageUri('');
        setMetadataUri('');
        setSteps({
            imageUploaded: false,
            metadataUploaded: false,
            serviceFeePaid: false,
            creatorFeePaid: false,
            nftCreated: false,
            setUpdateDelegate: false,
            setRoyalties: false,
            addedToNftList: false,
            addedToPageList: false,
        });

        if (!connected || !publicKey || !wallet?.adapter) {
            setError('Connect a wallet first.');
            return;
        }
        if (!comp.collectionId && !pageTokenListName) {
            setError('Please wait for the space token list to load before minting.');
            return;
        }
        if (!template?.name || !template?.description) {
            setError('Template is missing required fields (name/description).');
            return;
        }
        if (!spaceOwnerPk) {
            setError('The original creator could not be verified. Please reload this Space.');
            return;
        }
        if (digitalAssetOffer) {
            setError('Use Collect Digital Asset to mint this published edition.');
            return;
        }

        try {
            setMinting(true);
            const scope = 'component:' + spaceOwnerPk + ':' + comp.id;
            const applyCollectionResult = result => {
                setTxSig(result.signature || '');
                setAssetPubkey(result.assetAddress || '');
                if (result.cluster) setLegacyQuote(previous => ({ ...previous, cluster: result.cluster }));
                const confirmed = result.status === 'CONFIRMED';
                setSteps(s => ({ ...s, nftCreated: confirmed, setUpdateDelegate: confirmed, setRoyalties: confirmed,
                    serviceFeePaid: confirmed && BigInt(result.amounts?.serviceFeeAtomic || '0') > 0n,
                    creatorFeePaid: confirmed && BigInt(result.amounts?.creatorProceedsAtomic || '0') > 0n }));
                setStatus(confirmed ? 'NFT created in the selected collection. Your wallet is the owner.' : result.error || 'Mint transaction pending. Retry to check its confirmation.');
                if (confirmed) { clearMintUploads(mintUploads.current.key); mintUploads.current = {}; }
            };
            const pending = pendingPublishingOperation(publicKey.toBase58?.() || publicKey.toString(), scope);
            if (pending) {
                const prepared = await callDigitalAssets('confirmOperation', { requestId: pending }, walletContext);
                if (!['FAILED', 'EXPIRED'].includes(prepared.status)) {
                    applyCollectionResult(await executePublishingOperation(walletContext, prepared, scope, setStatus));
                    return;
                }
            }
            const quote = await callDigitalAssets('quoteLegacyMint', { ownerPublicKey: spaceOwnerPk, componentId: String(comp.id) }, walletContext);
            if (quote.creatorUserId !== spaceOwnerPk) throw new Error('Creator identity changed. Please reload.');
            if ((quote.collectionId || '') !== (comp.collectionId || '')) throw new Error('The published collection changed. Publish Updates or reload before minting.');
            setLegacyQuote(quote);
            if (comp.collectionId) {
                await assertPublishingNetwork(quote.cluster, rpcEndpoint);
                // Use the verified collection network immediately. This handler still
                // holds the Umi instance from before setLegacyQuote re-renders the panel.
                umi.use(irysUploader({ address: quote.cluster === 'devnet' ? DEVNET_IRYS : MAINNET_IRYS }));
            }

            // 1) prepare image
            const uploadKey = JSON.stringify([publicKey.toString(), spaceOwnerPk, comp.id, comp.collectionId || '', quote.cluster || '', template]);
            if (mintUploads.current.key !== uploadKey) mintUploads.current = readMintUploads(uploadKey) || { key: uploadKey };
            const uploads = mintUploads.current;
            let imgUri = uploads.imageUri;
            if (!imgUri) {
                setStatus('Preparing image…');
                const generic = await toGenericFile();

            // 2) upload image (Irys)
                setStatus('Uploading image to Irys…');
                [imgUri] = await umi.uploader.upload([generic]);
                if (!imgUri) throw new Error('Image upload failed.');
                uploads.imageUri = imgUri;
                uploads.contentType = generic.tags?.find(t => t.name === 'Content-Type')?.value || 'image/png';
                saveMintUploads(uploads);
            }
            setImageUri(imgUri);
            setSteps((s) => ({ ...s, imageUploaded: true }));

            // 3) upload metadata (Irys)
            const contentType = uploads.contentType;
            const metadata = {
                name: template.name,
                description: template.description,
                image: imgUri,
                external_url: template.externalUrl || '',
                attributes: Array.isArray(template.attributes)
                    ? template.attributes.map((a) => ({
                        trait_type: a.name || a.trait_type || 'Trait',
                        value: a.value ?? '',
                    }))
                    : [],
                properties: { files: [{ uri: imgUri, type: contentType }], category: 'image' },
            };

            setStatus('Uploading metadata to Irys…');
            const mdUri = uploads.metadataUri || await umi.uploader.uploadJson(metadata);
            if (!mdUri) throw new Error('Metadata upload failed.');
            uploads.metadataUri = mdUri;
            saveMintUploads(uploads);
            setMetadataUri(mdUri);
            setSteps((s) => ({ ...s, metadataUploaded: true }));

            if (comp.collectionId) {
                const prepared = await callDigitalAssets('prepareComponentMint', { ownerPublicKey: spaceOwnerPk, componentId: String(comp.id),
                    collectionId: comp.collectionId, metadataUri: mdUri, expectedPriceAtomic: quote.priceAtomic }, walletContext);
                applyCollectionResult(await executePublishingOperation(walletContext, prepared, scope, setStatus));
                return;
            }

            // 4) Build a single transaction with: fees -> create -> plugins
            setStatus('Building mint transaction…');
            const asset = generateSigner(umi);
            const creatorFee = Math.max(0, Number(comp?.creatorFeeSol ?? 0) || 0);

            let builder = transactionBuilder();
            if (BigInt(quote.serviceFeeAtomic) > 0n) {
                builder = builder.add(transferSol(umi, { destination: umiPublicKey(quote.treasuryWallet), amount: lamports(BigInt(quote.serviceFeeAtomic)) }));
            }
            if (BigInt(quote.creatorProceedsAtomic) > 0n) {
                builder = builder.add(transferSol(umi, { destination: umiPublicKey(spaceOwnerPk), amount: lamports(BigInt(quote.creatorProceedsAtomic)) }));
            }

            // Create Core NFT
            builder = builder.add(
                createCoreNft(umi, {
                    asset,
                    name: template.name,
                    uri: mdUri,
                    owner: umiPublicKey(publicKey.toString()),
                })
            );

            // Update Delegate -> MuultiSpace creator
            if (spaceOwnerPk) {
                builder = builder.add(
                    addPlugin(umi, {
                        asset: asset.publicKey,
                        plugin: {
                            type: 'UpdateDelegate',
                            authority: { type: 'Address', address: umiPublicKey(spaceOwnerPk) },
                            additionalDelegates: [],
                        },
                    })
                );
            }

            // Royalties (from comp.royalties). No service wallet in royalties.
            try {
                setStatus('Adding Royalties…');
                const { basisPoints, creators } = normalizeRoyalties(spaceOwnerPk, comp?.royalties, umiPublicKey);

                if (basisPoints > 0 && creators.length > 0) {
                    builder = builder.add(
                        addPlugin(umi, {
                            asset: asset.publicKey,
                            plugin: {
                                type: 'Royalties',
                                basisPoints,          // e.g. 700 = 7%
                                creators,             // integer shares summing to 100
                                ruleSet: { type: 'None' },
                            },
                        })
                    );
                }
            } catch (royErr) {
                console.warn('Could not add Royalties plugin:', royErr);
            }

            setStatus('Sending transaction…');
            const receipt = await builder.sendAndConfirm(umi);
            // Try to get a base58 string either way.
            const sig =
                typeof receipt === 'string'
                    ? receipt
                    : typeof receipt?.signature === 'string'
                    ? receipt.signature
                    : base58.deserialize(receipt?.signature || new Uint8Array())[0];

            setTxSig(sig);
            setAssetPubkey(asset.publicKey.toString());
            setSteps((s) => ({
                ...s,
                serviceFeePaid: BigInt(quote.serviceFeeAtomic) > 0n,
                creatorFeePaid: s.creatorFeePaid || (creatorFee > 0 && !!spaceOwnerPk),
                nftCreated: true,
                setUpdateDelegate: !!spaceOwnerPk,
                setRoyalties: true,
            }));

            // 5) Firestore gate lists
            const nftGateListName = `${template.name}TokenList`;
            await upsertGateListWithToken(nftGateListName, asset.publicKey.toString());
            setSteps((s) => ({ ...s, addedToNftList: true }));

            if (pageTokenListName) {
                const pageGateListName = pageTokenListName;
                await upsertGateListWithToken(pageGateListName, asset.publicKey.toString(), spaceOwnerPk);
                setSteps((s) => ({ ...s, addedToPageList: true }));
            }

            setStatus('NFT Created! Please wait up to 30 minutes for data to propagate on-chain.');
            clearMintUploads(mintUploads.current.key);
            mintUploads.current = {};
        } catch (e) {
            console.error(e);
            setStatus('');
            setError(e.message || 'Mint failed.');
        } finally {
            setMinting(false);
        }
    };

    // Reuse the publishing snapshot; these display values never set mint policy.
    const mintCollection = collections.find(item => item.id === (digitalAssetOffer?.collectionId || comp.collectionId));
    const mintMetadata = digitalAssetOffer ? { ...template, ...digitalAssetOffer } : template;
    const royaltyBasisPoints = digitalAssetOffer ? digitalAssetOffer.creatorConfiguration?.basisPoints
        : comp.collectionId && !comp.royalties ? mintCollection?.creatorConfiguration?.basisPoints
        : pctToBp(comp.royalties?.percentage ?? (comp.collectionId ? 0 : 7));
    const mintPriceLabel = digitalAssetOffer
        ? `${formatAtomic(digitalAssetOffer.priceAtomic, digitalAssetOffer.paymentDecimals)} ${digitalAssetOffer.paymentToken || 'SOL'}`
        : `${legacyQuote ? formatAtomic(legacyQuote.priceAtomic) : Math.max(0, Number(comp.creatorFeeSol) || 0)} SOL`;

    // typography helpers
    const typo = resolveTypography(pageTypography, style);
    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));
    const safeUsername = routeUsername || muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);
    const detailHref =
        safeUsername && comp?.id
            ? `/${safeUsername}/m/${comp.id}`
            : null;

    // ---- WRAPPING (like TextComponent) ----
    if (isLoading || loadingTemplate) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingTemplate} access={gateAccess}
            title={getPanelRevealTitle(comp, template?.name)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h3
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    Checking access for {gateNamesLabel}…
                </h3>
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
            <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} />
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    if (!hasAccess) {
        const directPaymentGate =  (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =  (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingTemplate} access={gateAccess}
            title={getPanelRevealTitle(comp, template?.name)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    {deniedHeading} - {gateNamesLabel}
            {passwordGate ? (
                <PasswordUnlock
                    gate={passwordGate}
                    componentId={comp.id}
                    onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                />
            ) : null}
            {directPaymentGate ? (
                <DirectPaymentUnlock
                    gate={directPaymentGate}
                    componentId={comp.id}
                    componentType={comp.type}
                    ownerPublicKey={ownerPublicKeyStr}
                    onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                />
            ) : null}
                </h1>
                {donationGateLinks.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {donationGateLinks.map((d) => (
                            <div key={d.id} style={{ marginTop: 4 }}>
                                <Link to={d.href} className="detail-page-return-link">
                                    View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
            <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} />
                <ComponentControls
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                    timestampDisplay={comp.timestampDisplay || 'none'}
                    onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading || loadingTemplate} access={gateAccess}
            title={getPanelRevealTitle(comp, template?.name)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
            <h2
                style={{
                    fontFamily: typo.headers.fontFamily,
                    fontSize: numToPx(typo.headers.fontSize),
                    fontWeight: typo.headers.fontWeight,
                    color: typo.headers.color,
                    lineHeight: typo.headers.lineHeight
                }}
            >
                {mintMetadata?.name || 'Mint NFT'}
            </h2>

            {error && (
                <p
                    className="error"
                    role="alert"
                    style={{
                        fontFamily: typo.paragraph.fontFamily,
                        fontSize: numToPx(typo.paragraph.fontSize),
                        color: typo.paragraph.color,
                        lineHeight: typo.paragraph.lineHeight
                    }}
                >
                    {error}
                </p>
            )}

            {fundingTxSig && <p><a href={`https://explorer.solana.com/tx/${fundingTxSig}?cluster=${isDevnet ? 'devnet' : 'mainnet-beta'}`}
                target="_blank" rel="noreferrer">View upload funding transaction: {fundingTxSig}</a></p>}

            {template && (
                <div style={{ textAlign: 'left' }}>
                    {template.imageUrl && (
                        <img
                            src={template.imageUrl}
                            alt={template.name}
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 12, marginBottom: 8 }}
                        />
                    )}

                    <div
                        style={{
                            marginTop: 4,
                            fontFamily: typo.paragraph.fontFamily,
                            fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color,
                            lineHeight: typo.paragraph.lineHeight
                        }}
                    >
                        <MintNftDetails metadata={mintMetadata} offer={digitalAssetOffer} priceLabel={mintPriceLabel}
                            royaltyBasisPoints={royaltyBasisPoints} collectionName={mintCollection?.name
                                || (comp.collectionId || digitalAssetOffer?.collectionId ? 'Creator-selected collection' : '')} />
                        {!digitalAssetOffer && legacyQuote && <div style={{ marginTop: 4 }}>
                            <strong>Included service commission:</strong> {formatAtomic(legacyQuote.serviceFeeAtomic)} SOL
                        </div>}
                    </div>

                    <div className="muulti-container-button-container" style={{ marginTop: 12 }} hidden={!!digitalAssetOffer}>
                        {isDevnet && (
                            <button
                                className="muulti-button"
                                onClick={handleAirdrop}
                                disabled={airdropping || !connected}
                                style={{ marginBottom: 8 }}
                            >
                                {airdropping ? 'Airdropping…' : 'Devnet Airdrop 1 SOL'}
                            </button>
                        )}
                        <button className="muulti-button" onClick={handleMint} disabled={minting || !connected || (!comp.collectionId && !pageTokenListName) || !spaceOwnerPk}>
                            {minting ? 'Minting…' : 'Mint NFT'}
                        </button>
                    </div>

                    {!digitalAssetOffer && <MintProgress
                        steps={getLegacyMintStages(steps, { collectionId: comp.collectionId, hasPageList: !!pageTokenListName })}
                        status={status} busy={minting} error={error}
                        style={{ fontFamily: typo.paragraph.fontFamily, fontSize: numToPx(typo.paragraph.fontSize),
                            color: typo.paragraph.color, lineHeight: typo.paragraph.lineHeight }} />}
                    {/* Mint result links */}
                    {txSig && (
                        <div style={{ marginTop: 8 }}>
                            <div><strong>Transaction:</strong></div>
                            <div>
                                <a
                                    href={`https://explorer.solana.com/tx/${txSig}?cluster=${isDevnet ? 'devnet' : 'mainnet'}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        color: typo.links.color,
                                        textDecoration: typo.links.textDecoration || undefined
                                    }}
                                >
                                    View in Solana Explorer
                                </a>
                            </div>
                        </div>
                    )}
                    {assetPubkey && (
                        <div style={{ marginTop: 8 }}>
                            <div><strong>Asset:</strong></div>
                            <div>
                                <a
                                    href={`https://core.metaplex.com/explorer/${assetPubkey}?env=${isDevnet ? 'devnet' : 'mainnet'}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        color: typo.links.color,
                                        textDecoration: typo.links.textDecoration || undefined
                                    }}
                                >
                                    View in Metaplex Explorer
                                </a>
                            </div>
                        </div>
                    )}
                    {(imageUri || metadataUri) && (
                        <div style={{ marginTop: 8 }}>
                            <div><strong>Uploads (Irys)</strong></div>
                            {imageUri && (
                                <div>
                                    Image:{' '}
                                    <a
                                        href={imageUri}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: typo.links.color,
                                            textDecoration: typo.links.textDecoration || undefined
                                        }}
                                    >
                                        {imageUri}
                                    </a>
                                </div>
                            )}
                            {metadataUri && (
                                <div>
                                    Metadata:{' '}
                                    <a
                                        href={metadataUri}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: typo.links.color,
                                            textDecoration: typo.links.textDecoration || undefined
                                        }}
                                    >
                                        {metadataUri}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    {detailHref && (
                        <div style={{ marginTop: 8 }}>
                            <Link to={detailHref} target="_blank" rel="noreferrer" style={{
                                    fontFamily: typo.links.fontFamily,
                                    fontSize: typeof typo.links.fontSize === 'number' ? `${typo.links.fontSize}px` : typo.links.fontSize,
                                    fontWeight: typo.links.fontWeight,
                                    color: typo.links.color,
                                    textDecoration: typo.links.textDecoration || 'underline',
                                    lineHeight: typo.links.lineHeight || 'normal',
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer'
                                }}
                            >
                                Open in new page →
                            </Link>
                        </div>
                    )}
                </div>
            )}
            {hasAccess && (
                <GateBadge gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            {/* ComponentControls (match TextComponent) */}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            <DigitalAssetPurchase comp={comp} ownerPublicKey={ownerPublicKeyStr} hideSummary={!!digitalAssetOffer} showMintProgress />
            <ComponentControls
                    onMintCollectionChange={onMintCollectionChange}
                    publishingComponent={comp}
                    publishingOwnerPublicKey={ownerPublicKeyStr}
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                timestampDisplay={comp.timestampDisplay || 'none'}
                onTimestampDisplayChange={val => onTimestampDisplayChange?.(val)}
            />
        </div>
        </GateRevealPanel>
    );
}
